/**
 * État partagé de la console tuteur : la file du matin et le périmètre d'élèves.
 *
 * Deux raisons d'exister, et pas une de plus :
 *
 *  1. **Les compteurs du rail viennent de la file.** « Ma journée 3 », « À
 *     valider 5 » sont `FileTuteurResponse.compteurs`, calculés par le serveur.
 *     Sans état partagé, chaque écran redemanderait la file pour afficher un
 *     badge de navigation.
 *  2. **La file se recharge après une écriture.** Une revue, un report, une
 *     reprise changent ce que le serveur renverrait : on redemande la file
 *     plutôt que de recomposer localement une priorisation qui est, par
 *     construction, une décision du serveur.
 *
 * `admin` n'appelle rien ici : `/v1/tuteur/*` est réservé au rôle `tuteur`
 * (403 sinon). La console admin garde donc sa route et ses écrans intacts.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import type { ElevesDuTuteurResponse, FileTuteurResponse } from '@silex/shared';

import { api } from '../api/client';
import { useAsyncRechargeable, type Resource } from '../lib/useAsync';

/* ------------------------------------------------- clôture locale de session */

const CLE_TRAITEES = 'silex.dashboard.tuteur.traitees';

interface TraiteesStockees {
  readonly jour: string;
  readonly cles: readonly string[];
}

/** Jour courant à Montréal, en `YYYY-MM-DD` — la file est une file du JOUR. */
function jourCourant(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Toronto' }).format(new Date());
}

function lireTraitees(): ReadonlySet<string> {
  try {
    const brut = window.sessionStorage.getItem(CLE_TRAITEES);
    if (!brut) return new Set();
    const parsed = JSON.parse(brut) as TraiteesStockees;
    if (parsed.jour !== jourCourant()) return new Set();
    return new Set(parsed.cles);
  } catch {
    return new Set();
  }
}

function ecrireTraitees(cles: ReadonlySet<string>): void {
  try {
    const charge: TraiteesStockees = { jour: jourCourant(), cles: [...cles] };
    window.sessionStorage.setItem(CLE_TRAITEES, JSON.stringify(charge));
  } catch {
    /* stockage indisponible — la marque vit alors le temps de la page */
  }
}

/* ------------------------------------------------------------------ contexte */

export interface TuteurContextValue {
  readonly file: Resource<FileTuteurResponse>;
  readonly rechargerFile: () => void;
  readonly eleves: Resource<ElevesDuTuteurResponse>;
  readonly rechargerEleves: () => void;
  /**
   * Unités marquées « traitée » dans cet onglet, aujourd'hui.
   *
   * Aucune route ne clôt une unité côté serveur : la file est RECALCULÉE à
   * chaque appel à partir des faits (une évaluation revue en sort d'elle-même,
   * un report la déplace). Pour les unités dont l'action n'a pas encore d'effet
   * serveur (créer des exercices, écrire le mot du tuteur), cette marque est le
   * « fait » du tuteur — et elle est annoncée comme locale à l'écran, jamais
   * présentée comme un état enregistré.
   */
  readonly traitees: ReadonlySet<string>;
  readonly marquerTraitee: (cle: string) => void;
  readonly rouvrirUnite: (cle: string) => void;
}

const TuteurContext = createContext<TuteurContextValue | null>(null);

export function TuteurProvider({ actif, children }: { actif: boolean; children: ReactNode }) {
  const fileLoader = useMemo(() => (actif ? () => api.getFileTuteur() : null), [actif]);
  const { resource: file, recharger: rechargerFile } = useAsyncRechargeable(fileLoader);

  const elevesLoader = useMemo(() => (actif ? () => api.getElevesDuTuteur() : null), [actif]);
  const { resource: eleves, recharger: rechargerEleves } = useAsyncRechargeable(elevesLoader);

  const [traitees, setTraitees] = useState<ReadonlySet<string>>(lireTraitees);

  const marquerTraitee = useCallback((cle: string) => {
    setTraitees((precedent) => {
      const suivant = new Set(precedent);
      suivant.add(cle);
      ecrireTraitees(suivant);
      return suivant;
    });
  }, []);

  const rouvrirUnite = useCallback((cle: string) => {
    setTraitees((precedent) => {
      const suivant = new Set(precedent);
      suivant.delete(cle);
      ecrireTraitees(suivant);
      return suivant;
    });
  }, []);

  const value = useMemo<TuteurContextValue>(
    () => ({
      file,
      rechargerFile,
      eleves,
      rechargerEleves,
      traitees,
      marquerTraitee,
      rouvrirUnite,
    }),
    [file, rechargerFile, eleves, rechargerEleves, traitees, marquerTraitee, rouvrirUnite],
  );

  return <TuteurContext.Provider value={value}>{children}</TuteurContext.Provider>;
}

export function useTuteur(): TuteurContextValue {
  const ctx = useContext(TuteurContext);
  if (!ctx) throw new Error('useTuteur doit être utilisé dans <TuteurProvider>');
  return ctx;
}
